import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";

import { isEmail } from "../validation/string";
import { createPendingCheckoutStarted } from "./customerio_pending_checkout";
import { Analytics, TrackProperties } from "./types";

const CHECKOUT_STARTED = "Checkout Started";
const ORDER_COMPLETED = "Order Completed";

export class CustomerioSourceProvider {
  initialize(customerioClient: CustomerioClient) {
    const cioanalytics = AnalyticsBrowser.load({
      writeKey: customerioClient.writeKey,
    });

    return CustomerioAnalytics(cioanalytics);
  }
}

interface CustomerioTrackerOptions {
  writeKey: string;
  sendPageView?: boolean;
}

export interface CustomerioClient extends CustomerioTrackerOptions {
  provider: "customerio";
}

export function makeCustomerioClient(
  tracker: CustomerioTrackerOptions
): CustomerioClient {
  return {
    provider: "customerio",
    writeKey: tracker.writeKey,
    sendPageView: tracker.sendPageView ?? true,
  };
}

/**
 * Customerio will only record where the source is WL. For events on FM, we don't include customerio in `allowedProviders` and for Portal
 * we exclude events with `source=Portal`
 */
function CustomerioAnalytics(sdk: AnalyticsBrowser): Analytics {
  let registeredProperties: { [key: string]: string } = {};
  const checkoutStarted = makeCheckoutStartedSender(sdk);

  // Deciding whether a checkout can go out means asking the SDK who it knows,
  // which waits until the SDK has loaded. Everything goes through this one chain
  // so an email given meanwhile is handled after that decision, and Customer.io
  // receives the calls in the order they were made. A failed call must not stop
  // the ones after it; what fails here is the vendor SDK in a visitor's browser,
  // which nobody can act on, so it isn't reported.
  let queue: Promise<unknown> = Promise.resolve();
  function enqueue(work: () => unknown) {
    queue = queue.then(work).catch(() => undefined);
  }

  return {
    group: () => {},
    register: (properties) => {
      Object.keys(properties).forEach((key) => {
        registeredProperties[key] = properties[key];
      });
    },

    // IMPORTANT
    // https://docs.customer.io/integrations/data-in/connections/javascript/js-source/#auto-identify
    identify: (userID, properties) => {
      const identifiedAs = userID || properties?.email;
      const partnerProperties = {
        partner_id: registeredProperties["Partner ID"],
        partner_name: registeredProperties["Partner Name"],
        partner_internal_name: registeredProperties["Partner Internal Name"],
      };

      enqueue(() => {
        if (identifiedAs) {
          sdk.identify(identifiedAs, { ...partnerProperties, properties });
        }

        sdk.track("Identify Subscription", partnerProperties);

        if (identifiedAs && isEmail(identifiedAs)) {
          checkoutStarted.onIdentified(identifiedAs);
        }
      });
    },
    screen: (name, properties = {}) => {
      const {
        partnerId,
        partnerName,
        partnerInternalName,
        pageName,
        template,
        catalogSlug,
        ...mainProperties
      } = properties;

      if (
        properties.Source === "Portal" ||
        registeredProperties.Source === "Portal"
      ) {
        return;
      }

      if (!partnerId || !partnerName || !pageName) {
        return;
      }

      const partnerProperties = {
        "Partner ID": partnerId,
        "Partner Name": partnerName,
        "Partner Internal Name": partnerInternalName || partnerName,
        "Page Name": pageName,
      };

      registeredProperties = {
        ...registeredProperties,
        ...partnerProperties,
        ...(catalogSlug && { "Catalog Slug": catalogSlug }),
      };

      const pageProperties = {
        ...registeredProperties,
        ...mainProperties,
        ...partnerProperties,
        Name: name,
        "Page URL": window.location.href,
        referrer: document.referrer,
      };

      enqueue(() => {
        sdk.page("Page View", name, pageProperties);
      });
    },
    ecommerceEvent: (event) => {
      const { event: eventName, ...properties } = event;
      const payload = { ...registeredProperties, ...properties };

      if (eventName === CHECKOUT_STARTED) {
        enqueue(() => checkoutStarted.onCheckoutStarted(payload));
        return;
      }

      enqueue(() => {
        if (eventName === ORDER_COMPLETED) {
          checkoutStarted.onOrderCompleted();
        }

        sdk.track(eventName, payload);
      });
    },
    event: (eventName, properties) => {
      if (
        properties?.Source === "Portal" ||
        registeredProperties.Source === "Portal"
      ) {
        return;
      }

      const payload = { ...registeredProperties, ...properties };

      enqueue(() => {
        sdk.track(eventName, payload);
      });
    },
  };
}

/**
 * `Checkout Started` only ever reaches Customer.io from a visitor it can name.
 * Its abandoned-cart automation starts on that event, and an anonymous one
 * waits on Customer.io attaching it to the person once they identify, which
 * sometimes happens days late or not at all. So the checkout of a visitor the
 * SDK doesn't know yet waits for their email and goes out under it.
 */
function makeCheckoutStartedSender(sdk: AnalyticsBrowser) {
  const pending = createPendingCheckoutStarted();
  // The checkout sent in this visit, and every email it has gone out under.
  let sent: { properties: TrackProperties; sentTo: Set<string> } | undefined;

  return {
    async onCheckoutStarted(properties: TrackProperties) {
      const knownId = (await sdk.user()).id();
      if (!knownId) {
        pending.save(properties);
        return;
      }

      // The newest checkout replaces any older one still waiting.
      pending.discard();
      sdk.track(CHECKOUT_STARTED, properties);
      sent = { properties, sentTo: new Set([knownId]) };
    },
    onIdentified(email: string) {
      const properties = pending.take();
      if (properties) {
        sdk.track(CHECKOUT_STARTED, properties, { userId: email });
        sent = { properties, sentTo: new Set([email]) };
        return;
      }

      if (!sent || sent.sentTo.has(email)) return;

      sdk.track(CHECKOUT_STARTED, sent.properties, { userId: email });
      sent.sentTo.add(email);
    },
    // Once the visitor has paid, neither a checkout still waiting nor the one
    // already sent may go out again: a later email would start reminders for a
    // cart that was already paid for.
    onOrderCompleted() {
      pending.discard();
      sent = undefined;
    },
  };
}
